import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { isApiError } from '@platform/api-client';
import {
  CRM_SYNC_EVENTS,
  type CrmMappedField,
  type CrmMapping,
  type CrmSourceField,
  type CrmSyncEvent,
} from '@platform/contracts';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Dialog,
  Field,
  Select,
  useToast,
} from '@platform/ui';

import { DataList, LoadingBlock } from '../../components/data';
import type { CrmMappingView } from '../../lib/api/integrations';
import { useIntegrationsClient } from '../../lib/api/integrations-client-context';

import {
  CRM_EVENT_HINTS,
  CRM_EVENT_LABELS,
  CRM_HIPAA_ATTESTATION,
  CRM_HIPAA_ATTESTATION_HINT,
  CRM_MAPPED_FIELD_LABELS,
  CRM_SOURCE_LABELS,
  crmIssuesToErrors,
  hasNoFieldErrors,
  type CrmFieldErrors,
} from './crm';
import { CrmTagEditor } from './crm-tag-editor';
import { formatMoment } from './status';

/**
 * The Marketing sync tab (MASTER_PLAN M3.12, FR-CRM-06, `INTEGRATIONS.md` §9A).
 *
 * Everything the clinic decides about its HighLevel sync, on one screen and in one save:
 * the attestation, the operational switch, the tags per platform event, and which platform
 * value fills each CRM custom field.
 *
 * ## The order of the cards is the order of the gates
 *
 * `CrmSyncService` runs install, then attestation, then marketing authorization, then the
 * operational switch. The screen is laid out the same way, so what an admin reads top to
 * bottom is what the sync actually asks, and the "nothing is syncing" answer is always
 * above the controls that would otherwise look like the problem.
 *
 * ## Why the mapping is choices and never text boxes
 *
 * FR-CRM-06 gives the clinic the field mapping, and a pair of text boxes would be a way to
 * put a diagnosis into a marketing system. The destinations are the four the API serves in
 * `mappedFields`, the sources are the ones it serves in `sourceFields`, and both come from
 * the response rather than from a constant here — adding a source field should be an API
 * release, not an API release plus a frontend release. This form cannot express a
 * disclosure, which is a stronger guarantee than validating one.
 *
 * Tags are the one free-text surface, and they are refused twice: for grammar by the
 * contract and for vocabulary by the CRM scrubber, at the moment of the save. A refusal
 * names the chip (`['tags', <event>, <index>]`) and lands on it.
 */

export interface CrmMappingFormProps {
  /** Refetch of the install, so a first attestation updates the status pill above. */
  onChanged?: () => void;
}

export function CrmMappingForm({ onChanged }: CrmMappingFormProps) {
  const client = useIntegrationsClient();
  const { toast } = useToast();

  const mapping = useQuery({
    queryKey: ['crm-mapping'],
    queryFn: () => client.getCrmMapping(),
    retry: false,
  });

  if (mapping.isPending) {
    return (
      <Card>
        <LoadingBlock label="Loading the marketing sync settings" rows={4} />
      </Card>
    );
  }

  if (mapping.isError) {
    const error = mapping.error;
    return (
      <Alert
        tone={isApiError(error) && error.code === 'NOT_FOUND' ? 'info' : 'error'}
        title={
          isApiError(error) && error.code === 'NOT_FOUND'
            ? 'HighLevel is not available for this clinic yet.'
            : 'These settings could not be loaded.'
        }
      >
        {error instanceof Error ? error.message : 'Try again in a moment.'}
      </Alert>
    );
  }

  /* Keyed on the loaded state, so a save that answers with a new `hipaaModeConfirmedAt`
     re-seeds the draft instead of leaving the form holding the values it just sent. */
  return (
    <CrmMappingEditor
      key={`${String(mapping.data.hipaaModeConfirmedAt)}-${String(mapping.dataUpdatedAt)}`}
      view={mapping.data}
      onSaved={() => {
        void mapping.refetch();
        onChanged?.();
      }}
      onQueued={(message, tone) => {
        toast({ tone, message });
      }}
    />
  );
}

/* ------------------------------------------------------------------ editor */

interface CrmMappingEditorProps {
  view: CrmMappingView;
  onSaved: () => void;
  onQueued: (message: string, tone: 'success' | 'warning' | 'error') => void;
}

function CrmMappingEditor({ view, onSaved, onQueued }: CrmMappingEditorProps) {
  const client = useIntegrationsClient();
  const { toast } = useToast();

  const [draft, setDraft] = useState<CrmMapping>(() => structuredClone(view.mapping));
  const [errors, setErrors] = useState<CrmFieldErrors>({ tags: {}, customFields: {} });
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [confirmingBackfill, setConfirmingBackfill] = useState(false);

  const attestedAt = view.hipaaModeConfirmedAt;
  /* The save has not happened yet, so the *effective* gate is what is stored, not what is
     ticked: ticking the box does not start the sync, saving does. */
  const syncing = view.enabled && view.mapping.hipaaModeConfirmed;

  const save = async (): Promise<void> => {
    setSaving(true);
    setErrors({ tags: {}, customFields: {} });
    setFormError(undefined);
    try {
      await client.putCrmMapping(draft);
      toast({ tone: 'success', message: 'Marketing sync settings saved.' });
      onSaved();
    } catch (error) {
      if (isApiError(error) && error.code === 'VALIDATION_FAILED') {
        const mapped = crmIssuesToErrors(error.issues);
        setErrors(mapped);
        setFormError(mapped.form ?? (hasNoFieldErrors(mapped) ? error.message : undefined));
      } else {
        setFormError(isApiError(error) ? error.message : 'That could not be saved. Try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const backfill = async (): Promise<void> => {
    setBackfilling(true);
    try {
      const result = await client.crmSyncNow();
      setConfirmingBackfill(false);
      onQueued(
        result.queued
          ? `Backfill queued, ${String(result.pageSize)} patients at a time. Progress shows in the activity log.`
          : (result.reason ?? 'Nothing was queued.'),
        result.queued ? 'success' : 'warning',
      );
    } catch (error) {
      onQueued(isApiError(error) ? error.message : 'The backfill could not be started.', 'error');
    } finally {
      setBackfilling(false);
    }
  };

  const setTags = (event: CrmSyncEvent, tags: string[]): void => {
    setDraft((current) => ({ ...current, tags: { ...current.tags, [event]: tags } }));
    setErrors((current) => {
      const { [event]: _cleared, ...rest } = current.tags;
      return { ...current, tags: rest };
    });
  };

  const setSource = (field: CrmMappedField, source: CrmSourceField): void => {
    setDraft((current) => ({
      ...current,
      customFields: { ...current.customFields, [field]: source },
    }));
    setErrors((current) => {
      const { [field]: _cleared, ...rest } = current.customFields;
      return { ...current, customFields: rest };
    });
  };

  return (
    <div className="flex flex-col gap-6">
      {/* ------------------------------------------------------- what syncs */}
      <Alert
        tone={syncing ? 'success' : 'warning'}
        title={syncing ? 'Marketing sync is running.' : 'Nothing is syncing to HighLevel yet.'}
      >
        {syncing ? (
          <>
            Marketing tags and the mapped fields go only to patients who have an active marketing
            authorization on file. Everyone else is{' '}
            {draft.operationalSyncEnabled
              ? 'synced with appointment facts only, with do-not-disturb switched on in HighLevel'
              : 'not sent to HighLevel at all'}
            .
          </>
        ) : !view.installed || !view.enabled ? (
          'Connect HighLevel on the Credentials tab first. Nothing on this page takes effect until it is connected and turned on.'
        ) : (
          'Confirm HighLevel’s HIPAA mode below and save. Until then nothing that identifies a patient is sent — not even a name.'
        )}
      </Alert>

      {formError === undefined ? null : <Alert tone="error">{formError}</Alert>}

      <form
        className="flex flex-col gap-6"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        {/* -------------------------------------------------- 1. attestation */}
        <Card>
          <CardHeader
            as="h2"
            title="HIPAA mode and the BAA"
            subtitle="Only your clinic can see HighLevel's HIPAA switch, so only your clinic can confirm it."
          />
          <div className="flex flex-col gap-5 px-6 pt-5 pb-6 max-[480px]:px-4">
            <Checkbox
              label={CRM_HIPAA_ATTESTATION}
              description={CRM_HIPAA_ATTESTATION_HINT}
              checked={draft.hipaaModeConfirmed}
              disabled={saving}
              onChange={(event) => {
                const confirmed = event.currentTarget.checked;
                setDraft((current) => ({ ...current, hipaaModeConfirmed: confirmed }));
              }}
            />

            <DataList
              items={[
                {
                  label: 'BAA recorded',
                  value:
                    view.baaSignedAt === null ? (
                      <span className="text-neutral-400-text">Not recorded yet</span>
                    ) : (
                      formatMoment(view.baaSignedAt)
                    ),
                },
                {
                  /*
                   * Two facts, and they can disagree. The instant is the record of the
                   * attestation and is never rewound; the box above is the clinic's answer
                   * *now* and can be cleared. Showing a green "Confirmed" off an instant
                   * that a cleared box has already overruled would say the sync is running
                   * when it is not — so the badge follows the saved answer and the instant
                   * is reported beside it either way.
                   */
                  label: 'HIPAA mode confirmed',
                  value:
                    attestedAt === null ? (
                      <span className="text-neutral-400-text">Not confirmed yet</span>
                    ) : view.mapping.hipaaModeConfirmed ? (
                      <span className="flex flex-wrap items-center gap-2">
                        <Badge tone="success">Confirmed</Badge>
                        <time dateTime={attestedAt} className="tabular-nums">
                          {formatMoment(attestedAt)}
                        </time>
                      </span>
                    ) : (
                      <span className="flex flex-wrap items-center gap-2">
                        <Badge tone="warning">Not confirmed now</Badge>
                        <span className="text-neutral-500">
                          first recorded{' '}
                          <time dateTime={attestedAt} className="tabular-nums">
                            {formatMoment(attestedAt)}
                          </time>
                        </span>
                      </span>
                    ),
                },
              ]}
            />

            <p className="text-sm text-neutral-500">
              The moment you first confirm is kept as the record of the attestation and is not
              erased if you later clear the box. Clearing it stops the sync straight away.
            </p>
          </div>
        </Card>

        {/* ------------------------------------------- 2. who gets synced */}
        <Card>
          <CardHeader
            as="h2"
            title="Patients without a marketing authorization"
            subtitle="Marketing fields only ever go to patients who agreed to marketing. This decides what happens to everyone else."
          />
          <div className="flex flex-col gap-4 px-6 pt-5 pb-6 max-[480px]:px-4">
            <Checkbox
              label="Send appointment facts for patients who have not agreed to marketing"
              description="Name, one contact point, the next appointment date and the last appointment status, with do-not-disturb switched on for every channel in HighLevel. Off by default, and off means nothing about them leaves the platform."
              checked={draft.operationalSyncEnabled}
              disabled={saving}
              onChange={(event) => {
                const enabled = event.currentTarget.checked;
                setDraft((current) => ({ ...current, operationalSyncEnabled: enabled }));
              }}
            />
            <p className="text-sm text-neutral-500">
              A patient who agreed and later withdrew is handled either way: the marketing tags are
              taken off their HighLevel contact and do-not-disturb is switched on, so your campaigns
              cannot reach them.
            </p>
          </div>
        </Card>

        {/* ------------------------------------------------------- 3. tags */}
        <Card>
          <CardHeader
            as="h2"
            title="Tags by event"
            subtitle="Applied in HighLevel when a visit reaches each state — and only to patients with an active marketing authorization."
          />
          <div className="flex flex-col gap-6 px-6 pt-5 pb-6 max-[480px]:px-4">
            <p className="text-base text-neutral-700">
              Lower-case letters, digits, <code className="font-mono">-</code>,{' '}
              <code className="font-mono">_</code> and <code className="font-mono">:</code>. A tag
              is a campaign filter, not a note: anything naming a condition, a medication or a date
              is refused when you save.
            </p>
            {CRM_SYNC_EVENTS.map((event) => (
              <CrmTagEditor
                key={event}
                label={CRM_EVENT_LABELS[event]}
                help={CRM_EVENT_HINTS[event]}
                tags={draft.tags[event]}
                disabled={saving}
                {...(errors.tags[event] === undefined ? {} : { errors: errors.tags[event] })}
                onChange={(next) => {
                  setTags(event, next);
                }}
              />
            ))}
          </div>
        </Card>

        {/* ----------------------------------------------- 4. custom fields */}
        <Card>
          <CardHeader
            as="h2"
            title="Custom fields"
            subtitle="Which platform value fills each HighLevel custom field. The lists are fixed by the platform."
          />
          <div className="flex flex-col gap-5 px-6 pt-5 pb-6 max-[480px]:px-4">
            {view.mappedFields.map((field) => (
              <Field
                key={field}
                label={CRM_MAPPED_FIELD_LABELS[field] ?? field}
                {...(errors.customFields[field] === undefined
                  ? {}
                  : { error: errors.customFields[field] })}
              >
                <Select
                  value={draft.customFields[field]}
                  disabled={saving}
                  onChange={(event) => {
                    setSource(field, event.currentTarget.value as CrmSourceField);
                  }}
                >
                  {view.sourceFields.map((source) => (
                    <option key={source} value={source}>
                      {CRM_SOURCE_LABELS[source] ?? source}
                    </option>
                  ))}
                </Select>
              </Field>
            ))}
            <p className="text-sm text-neutral-500">
              Only “Next appointment date” may be filled from an appointment date; every other field
              refuses anything date-shaped, so a mix-up is caught here rather than by a failing
              sync.
            </p>
          </div>
        </Card>

        <div>
          <Button type="submit" disabled={saving} loading={saving} loadingLabel="Saving">
            Save marketing sync
          </Button>
        </div>
      </form>

      {/* ------------------------------------------------------- 5. backfill */}
      <Card>
        <CardHeader
          as="h2"
          title="Bring existing patients across"
          subtitle="A one-off walk through this clinic's patients, a page at a time, honouring the same consent rules."
        />
        <div className="flex flex-wrap items-center justify-between gap-4 px-6 pt-5 pb-6 max-[480px]:px-4">
          <p className="min-w-[14rem] grow text-base text-neutral-700">
            New bookings sync on their own. Use this once, after connecting, to send the patients
            you already have.
          </p>
          <Button
            variant="secondary"
            disabled={backfilling}
            onClick={() => {
              setConfirmingBackfill(true);
            }}
          >
            Sync existing patients
          </Button>
        </div>
      </Card>

      <Dialog
        open={confirmingBackfill}
        onClose={() => {
          setConfirmingBackfill(false);
        }}
        title="Sync existing patients to HighLevel?"
        description="This queues a background job. It runs a page at a time so it cannot use up HighLevel's rate limit while a booking is waiting to sync."
        footer={
          <>
            <Button
              variant="secondary"
              disabled={backfilling}
              onClick={() => {
                setConfirmingBackfill(false);
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={backfilling}
              loading={backfilling}
              loadingLabel="Queueing"
              onClick={() => {
                void backfill();
              }}
            >
              Queue the backfill
            </Button>
          </>
        }
      >
        <p className="text-base text-neutral-700">
          Patients with an active marketing authorization get the tags and mapped fields above.
          Everyone else gets{' '}
          {view.mapping.operationalSyncEnabled
            ? 'appointment facts only, with do-not-disturb switched on'
            : 'nothing — they are skipped'}
          .
        </p>
      </Dialog>
    </div>
  );
}
